import { logException } from '../log';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import { EventQueueMetrics } from './EventQueueMetrics';
import { v4 as uuidv4 } from 'uuid';

/**
 * EventQueue manages sequential or parallel event processing for a specific event type.
 * When enabled, events are processed sequentially (one at a time).
 * When disabled, events are processed in parallel (fire-and-forget).
 */
interface EventWithTimestamp {
  timestamp: number | string;
  type: string;
  [key: string]: any;
}

interface LastEventInfo {
  timestamp: number;
  type: string;
  event: EventWithTimestamp;
}

export class EventQueue {
  private queue: Promise<void> | null = null;
  private readonly queueName: string;
  private lastEvent: LastEventInfo | null = null;
  private pendingEventCount = 0;
  private readonly metrics: EventQueueMetrics;
  private isShuttingDown = false;

  constructor(queueName: string) {
    this.queueName = queueName;
    this.metrics = new EventQueueMetrics(queueName);
  }

  /**
   * Initialize the queue with the specified mode
   * @param isEnabled - Whether sequential processing is enabled
   * @param isReportEnabled - Whether metrics reporting is enabled
   */
  private initialize(isEnabled: boolean, isReportEnabled: boolean): void {
    this.queue = isEnabled ? Promise.resolve() : null;
    // Enable/disable metrics based on report flag
    if (isReportEnabled) {
      this.metrics.enable();
    } else {
      this.metrics.disable();
    }
  }

  /**
   * Create and initialize an EventQueue
   * @param queueName - Name of the queue for logging
   * @param isEnabled - Whether sequential processing is enabled
   * @param isReportEnabled - Whether metrics reporting is enabled
   * @returns Initialized EventQueue instance
   */
  static create(
    queueName: string,
    isEnabled: boolean,
    isReportEnabled: boolean,
  ): EventQueue {
    const queue = new EventQueue(queueName);
    queue.initialize(isEnabled, isReportEnabled);
    return queue;
  }

  private isSameEntity(
    prev: EventWithTimestamp,
    curr: EventWithTimestamp,
  ): boolean {
    return (
      (!!prev.queueId && !!curr.queueId && prev.queueId === curr.queueId) ||
      (!!prev.agentId && !!curr.agentId && prev.agentId === curr.agentId) ||
      (!!prev.conversationId &&
        !!curr.conversationId &&
        prev.conversationId === curr.conversationId)
    );
  }

  /**
   * Check for out-of-order events and track the last event
   * @param event - The event to check and track
   */
  private checkAndTrackEventOrder(event: EventWithTimestamp): void {
    // Normalize timestamp to number for comparison
    const currentTimestamp =
      typeof event.timestamp === 'string'
        ? new Date(event.timestamp).getTime()
        : event.timestamp;

    if (
      this.lastEvent !== null &&
      currentTimestamp < this.lastEvent.timestamp &&
      this.isSameEntity(this.lastEvent.event, event)
    ) {
      const message = `Out of order event detected in ${this.queueName} events queue`;
      const timeDiff = this.lastEvent.timestamp - currentTimestamp;
      console.warn(message, {
        previousEvent: this.lastEvent,
        currentEvent: {
          type: event.type,
          timestamp: currentTimestamp,
          event: event,
        },
        timeDifferenceMs: timeDiff,
      });
      DiagnosticsModule.reportBackgroundError(message, {
        source: 'EventQueue',
        priority: 'normal',
        extra: {
          queue: this.queueName,
          previousEvent: {
            type: this.lastEvent.type,
            timestamp: this.lastEvent.timestamp,
            event: this.lastEvent.event,
          },
          currentEvent: {
            type: event.type,
            timestamp: currentTimestamp,
            event: event,
          },
          timeDifferenceMs: timeDiff,
        },
      });
    }

    this.lastEvent = {
      timestamp: currentTimestamp,
      type: event.type,
      event: event,
    };
  }

  /**
   * Process an event handler either sequentially or in parallel
   * depending on whether the queue is enabled.
   *
   * @param handler - The async function to execute
   * @param event - Optional event object with timestamp and type to detect out-of-order events
   * @param isWebsocketClosed - Function that returns whether the websocket connection is closed
   */
  async process(
    handler: () => Promise<void>,
    event?: EventWithTimestamp,
    isWebsocketClosed?: () => boolean,
  ): Promise<void> {
    // Skip queueing if websocket is already closed (evaluate at queue time)
    if (isWebsocketClosed?.()) {
      return;
    }

    // Skip processing if queue is shutting down
    if (this.isShuttingDown) {
      return;
    }

    // Check for out-of-order events
    if (event?.timestamp !== undefined && event?.type !== undefined) {
      this.checkAndTrackEventOrder(event);
    }

    if (this.queue) {
      // Use event ID if available, otherwise generate one for tracking
      const eventId = event?.id ?? uuidv4();

      // Increment pending event count
      this.pendingEventCount++;

      // Record metrics
      this.metrics.recordEventQueued(event?.type);

      // Sequential mode: chain onto existing queue
      this.queue = this.queue
        .then(async () => {
          // Skip execution if websocket closed after this was queued
          if (isWebsocketClosed?.()) {
            return;
          }

          // Record processing started
          this.metrics.recordProcessingStarted(eventId);

          // Explicitly await to ensure sequential processing
          // even if callers forget to await their handlers
          await handler();
        })
        .catch((err) => {
          logException(err, `${this.queueName}.event.processing`);
        })
        .finally(() => {
          // Record processing completed
          this.metrics.recordProcessingCompleted(eventId, event?.type);

          // Decrement pending event count when done
          this.pendingEventCount--;
        });
    } else {
      // Parallel mode: fire and forget with error handling
      handler().catch((err) => {
        logException(err, `${this.queueName}.event.processing`);
      });
    }
  }

  /**
   * Reset the queue, waiting for all pending operations to complete first.
   * This ensures in-flight events finish processing before the queue is cleared.
   */
  async reset(): Promise<void> {
    // Prevent new events from being processed
    this.isShuttingDown = true;

    // Store current queue
    const currentQueue = this.queue;
    this.queue = null;

    // Wait for all pending events to complete
    if (currentQueue) {
      await currentQueue.catch(() => {
        // Ignore errors during shutdown - they've already been logged
      });
    }

    // Now safe to reset state
    this.lastEvent = null;
    this.pendingEventCount = 0;
    this.metrics.disable();
    this.isShuttingDown = false;
  }

  /**
   * Check if sequential processing is enabled for this queue
   */
  isEnabled(): boolean {
    return this.queue !== null;
  }
}
